/**
 * ChartTab — Monthly tachograph chart view.
 *
 * Shows every day in the loaded period as a 24-hour activity strip
 * (TachoChart), grouped by month. Also includes a stacked area chart
 * of daily hours by activity type.
 */

import { useState, useMemo } from 'react'
import { DriverAnalysis, ActivityRecord, DailySummary } from '../types'
import { TachoChart } from '../components/TachoChart'
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'

interface Props {
  analysis: DriverAnalysis
}

export function ChartTab({ analysis }: Props) {
  const { daily_breakdown, activities } = analysis
  const allActivities = (activities ?? []) as ActivityRecord[]

  // Group days by month
  const monthGroups = useMemo(() => {
    const groups: Record<string, DailySummary[]> = {}
    for (const day of daily_breakdown) {
      const month = day.date.slice(0, 7)
      ;(groups[month] ??= []).push(day)
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
  }, [daily_breakdown])

  const [selectedMonth, setSelectedMonth] = useState<string>(
    monthGroups.length > 0 ? monthGroups[monthGroups.length - 1][0] : ''
  )

  const currentDays = monthGroups.find(([m]) => m === selectedMonth)?.[1] ?? []

  // Data for stacked area chart
  const areaData = useMemo(() =>
    daily_breakdown.map((d) => ({
      date: d.date.slice(5),
      fullDate: d.date,
      Driving: +(d.driving_minutes / 60).toFixed(1),
      Work: +(d.work_minutes / 60).toFixed(1),
      Availability: +(d.availability_minutes / 60).toFixed(1),
      Rest: +(d.rest_minutes / 60).toFixed(1),
    })),
  [daily_breakdown])

  return (
    <div className="space-y-5">
      {/* Stacked area chart */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Activity Hours Over Time</h3>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={areaData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ fontSize: 11, borderRadius: 4, border: '1px solid #e5e7eb' }}
              formatter={(v: number, name: string) => [`${v}h`, name]}
              labelFormatter={(l: string) => {
                const full = areaData.find((d) => d.date === l)?.fullDate
                return full
                  ? new Date(full).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })
                  : l
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="Rest" stackId="1" fill="#e2e5ea" stroke="#d1d5db" fillOpacity={0.9} />
            <Area type="monotone" dataKey="Availability" stackId="1" fill="#94a3b8" stroke="#94a3b8" fillOpacity={0.5} />
            <Area type="monotone" dataKey="Work" stackId="1" fill="#d97706" stroke="#d97706" fillOpacity={0.8} />
            <Area type="monotone" dataKey="Driving" stackId="1" fill="#1d4ed8" stroke="#1d4ed8" fillOpacity={0.8} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Month picker */}
      <div className="flex items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Tachograph Charts</h3>
        <div className="flex gap-1 ml-3">
          {monthGroups.map(([month]) => {
            const label = new Date(month + '-01').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
            return (
              <button
                key={month}
                onClick={() => setSelectedMonth(month)}
                className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                  month === selectedMonth
                    ? 'bg-slate-700 text-white border-slate-700'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px] text-gray-400">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-700 rounded-sm" /> Driving</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2.5 bg-amber-600 rounded-sm" /> Work</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2 bg-slate-400 rounded-sm" /> Availability</span>
        <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded-sm" style={{ background: '#e2e5ea' }} /> Rest</span>
        <span className="ml-auto text-gray-300">Each row = 24h (00:00–24:00). Bar height = activity type.</span>
      </div>

      {/* Day-by-day tacho strips */}
      <div className="space-y-1">
        {currentDays.map((day) => {
          const dow = new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short' })
          const dateStr = new Date(day.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
          const vehicle = day.vehicles?.[0]?.reg

          return (
            <div key={day.date} className="flex items-center gap-3 hover:bg-gray-50 rounded px-2 py-0.5 transition-colors">
              <div className="w-14 flex-shrink-0 text-right">
                <span className="text-xs font-medium text-gray-700">{dow}</span>
                <span className="text-[10px] text-gray-400 ml-1">{dateStr}</span>
              </div>
              <div className="flex-1">
                <TachoChart activities={allActivities} date={day.date} height={24} showVehicle={vehicle} />
              </div>
              <div className="w-16 flex-shrink-0 text-right">
                <span className="text-xs font-medium text-gray-700">{day.driving_hours}h</span>
                {day.distance_km > 0 && <span className="text-[10px] text-gray-400 ml-1">{day.distance_km}km</span>}
              </div>
              <div className="w-4 flex-shrink-0">
                {day.status === 'infringement' && <span className="w-2 h-2 bg-red-500 rounded-full block" />}
                {day.status === 'warning' && <span className="w-2 h-2 bg-amber-500 rounded-full block" />}
              </div>
            </div>
          )
        })}
      </div>

      {/* Hour axis reference */}
      <div className="flex items-center gap-3 px-2">
        <div className="w-14" />
        <div className="flex-1 relative h-3">
          {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((h) => (
            <span
              key={h}
              className="absolute text-gray-300"
              style={{ fontSize: '9px', left: `${(h / 24) * 100}%`, transform: 'translateX(-50%)' }}
            >
              {String(h).padStart(2, '0')}:00
            </span>
          ))}
        </div>
        <div className="w-16" />
        <div className="w-4" />
      </div>
    </div>
  )
}
